import { SkillCategory } from '../../types/skillProgress';

export const tennisSkillCategories: SkillCategory[] = [
  {
    id: 'serving',
    name: 'Serving',
    description: 'Master the art of serving in tennis',
    skills: [
      {
        id: 'basic-serve',
        name: 'Basic Serve',
        description: 'Execute a legal overhand serve that clears the net',
        difficulty: 'beginner',
        tips: [
          'Use a continental grip',
          'Toss the ball consistently',
          'Follow through across your body',
          'Keep your head up during contact'
        ]
      },
      {
        id: 'serve-placement',
        name: 'Serve Placement',
        description: 'Place serves to different areas of the service box',
        difficulty: 'beginner',
        prerequisites: ['basic-serve'],
        tips: [
          'Aim for the corners to create angles',
          'Mix up wide, body, and T serves',
          'Use the court geometry to your advantage',
          'Practice consistency over power'
        ]
      },
      {
        id: 'power-serve',
        name: 'Power Serve',
        description: 'Add pace and spin to your serve',
        difficulty: 'intermediate',
        prerequisites: ['basic-serve', 'serve-placement'],
        tips: [
          'Generate power from your legs and core',
          'Use proper weight transfer',
          'Add topspin for more consistent power',
          'Practice timing and rhythm'
        ]
      },
      {
        id: 'spin-serve',
        name: 'Spin Serve',
        description: 'Master different types of spin serves',
        difficulty: 'advanced',
        prerequisites: ['power-serve'],
        tips: [
          'Practice topspin, slice, and kick serves',
          'Use spin to control bounce height',
          'Vary spin to keep opponents guessing',
          'Focus on consistency over power'
        ]
      }
    ]
  },
  {
    id: 'groundstrokes',
    name: 'Groundstrokes',
    description: 'Develop consistent groundstroke technique',
    skills: [
      {
        id: 'forehand-basic',
        name: 'Basic Forehand',
        description: 'Execute a proper forehand groundstroke',
        difficulty: 'beginner',
        tips: [
          'Use a semi-western or western grip',
          'Keep your paddle face square to the ball',
          'Follow through across your body',
          'Stay balanced throughout the stroke'
        ]
      },
      {
        id: 'backhand-basic',
        name: 'Basic Backhand',
        description: 'Execute a proper backhand groundstroke',
        difficulty: 'beginner',
        tips: [
          'Use a continental or eastern backhand grip',
          'Keep your non-dominant hand on the racket',
          'Rotate your shoulders for power',
          'Follow through toward your target'
        ]
      },
      {
        id: 'groundstroke-placement',
        name: 'Groundstroke Placement',
        description: 'Place groundstrokes to different areas of the court',
        difficulty: 'intermediate',
        prerequisites: ['forehand-basic', 'backhand-basic'],
        tips: [
          'Aim for the corners and sidelines',
          'Use cross-court shots to create angles',
          'Hit behind your opponent when they\'re moving',
          'Practice depth control'
        ]
      },
      {
        id: 'power-groundstroke',
        name: 'Power Groundstroke',
        description: 'Add pace and power to groundstrokes',
        difficulty: 'intermediate',
        prerequisites: ['groundstroke-placement'],
        tips: [
          'Generate power from your legs and core',
          'Use proper weight transfer',
          'Keep your head still during contact',
          'Follow through completely'
        ]
      }
    ]
  },
  {
    id: 'net-play',
    name: 'Net Play',
    description: 'Master volleys and overheads at the net',
    skills: [
      {
        id: 'volley-basic',
        name: 'Basic Volley',
        description: 'Execute a proper volley at the net',
        difficulty: 'intermediate',
        tips: [
          'Keep your racket up and ready',
          'Use a short, compact swing',
          'Step into the ball',
          'Keep your eyes on the ball'
        ]
      },
      {
        id: 'volley-placement',
        name: 'Volley Placement',
        description: 'Place volleys to different areas of the court',
        difficulty: 'intermediate',
        prerequisites: ['volley-basic'],
        tips: [
          'Aim for the corners of the court',
          'Use angles to create openings',
          'Mix up placement to keep opponents guessing',
          'Focus on consistency over power'
        ]
      },
      {
        id: 'overhead-smash',
        name: 'Overhead Smash',
        description: 'Execute a powerful overhead shot',
        difficulty: 'advanced',
        prerequisites: ['volley-basic'],
        tips: [
          'Get into position quickly',
          'Use a continental grip',
          'Hit the ball at the highest point',
          'Follow through toward your target'
        ]
      },
      {
        id: 'approach-shot',
        name: 'Approach Shot',
        description: 'Hit an approach shot to set up net play',
        difficulty: 'advanced',
        prerequisites: ['volley-placement'],
        tips: [
          'Hit deep to the corner',
          'Follow your shot to the net',
          'Use the right shot for the right situation',
          'Be ready to volley the return'
        ]
      }
    ]
  },
  {
    id: 'strategy',
    name: 'Strategy & Tactics',
    description: 'Develop strategic thinking and court awareness',
    skills: [
      {
        id: 'court-positioning',
        name: 'Court Positioning',
        description: 'Understand proper court positioning',
        difficulty: 'beginner',
        tips: [
          'Stay in the center of your side',
          'Move as a team in doubles',
          'Cover your partner\'s weaknesses',
          'Anticipate your opponent\'s shots'
        ]
      },
      {
        id: 'shot-selection',
        name: 'Shot Selection',
        description: 'Choose the right shot for each situation',
        difficulty: 'intermediate',
        prerequisites: ['court-positioning'],
        tips: [
          'Consider your opponent\'s position',
          'Use the right shot for the right situation',
          'Don\'t try to win every point with power',
          'Be patient and wait for opportunities'
        ]
      },
      {
        id: 'return-strategy',
        name: 'Return Strategy',
        description: 'Develop effective return strategies',
        difficulty: 'intermediate',
        prerequisites: ['shot-selection'],
        tips: [
          'Get the ball back in play',
          'Aim for the server\'s feet',
          'Use the return to take control',
          'Be ready to move forward'
        ]
      },
      {
        id: 'advanced-strategy',
        name: 'Advanced Strategy',
        description: 'Develop sophisticated strategic thinking',
        difficulty: 'advanced',
        prerequisites: ['return-strategy'],
        tips: [
          'Study your opponent\'s patterns',
          'Use deception and variety',
          'Control the pace of the game',
          'Exploit your opponent\'s weaknesses'
        ]
      }
    ]
  },
  {
    id: 'mental-game',
    name: 'Mental Game',
    description: 'Develop mental toughness and focus',
    skills: [
      {
        id: 'focus-concentration',
        name: 'Focus & Concentration',
        description: 'Maintain focus throughout the match',
        difficulty: 'beginner',
        tips: [
          'Stay in the present moment',
          'Don\'t dwell on mistakes',
          'Focus on the next point',
          'Use breathing techniques to stay calm'
        ]
      },
      {
        id: 'pressure-handling',
        name: 'Pressure Handling',
        description: 'Perform well under pressure',
        difficulty: 'intermediate',
        prerequisites: ['focus-concentration'],
        tips: [
          'Practice pressure situations',
          'Develop pre-shot routines',
          'Stay positive and confident',
          'Use visualization techniques'
        ]
      },
      {
        id: 'match-strategy',
        name: 'Match Strategy',
        description: 'Develop and execute match strategies',
        difficulty: 'advanced',
        prerequisites: ['pressure-handling'],
        tips: [
          'Adapt your strategy during the match',
          'Exploit your opponent\'s weaknesses',
          'Stay patient and disciplined',
          'Make adjustments as needed'
        ]
      }
    ]
  }
];
